import { useEffect, useRef, useState } from 'react';
import { FOCUS_BASE_URL } from '../constants';
import { focusModeApi } from '../../../services/api/focusMode.api';

export type FocusSubType = 'box' | 'tableau' | 'salesforce';

// Box items as returned by /focus_mode/user_accessible_items, normalized once
// at the fetch boundary (item_id/item_type/name/parent_id/path_display/size/
// modified_at) so the rest of the app works with one consistent shape instead
// of guessing at field names per call site.
export interface BoxItem {
  id: string;
  type: 'file' | 'folder';
  name: string;
  parentId: string | null;
  pathDisplay: string;
  sizeBytes: number | null;
  modifiedAt: string | null;
}

const normalizeBoxItem = (raw: any): BoxItem => ({
  id: String(raw?.item_id ?? raw?.id ?? ''),
  type: String(raw?.item_type ?? raw?.type ?? '').toLowerCase() === 'folder' ? 'folder' : 'file',
  name: String(raw?.name ?? 'Unnamed'),
  parentId: raw?.parent_id != null && raw.parent_id !== '' ? String(raw.parent_id) : null,
  pathDisplay: String(raw?.path_display ?? ''),
  sizeBytes: typeof raw?.size === 'number' ? raw.size : null,
  modifiedAt: raw?.modified_at ?? null,
});

// One folder in the Box breadcrumb trail (below the "Box" root).
export interface BoxPathItem {
  id: string;
  name: string;
}

// A committed Box pick: 'none' (nothing chosen — treated as unrestricted,
// same as 'all' payload-wise), 'all' (explicit "Box: Use files available"), or a
// specific folder/file. Picking happens as a staged, local choice inside
// BoxPanel and only lands here via submitBoxSelection (its Submit button).
// path: for a folder/file, the folders leading to it (the breadcrumb at the
// moment it was picked; empty at the top level) — lets the picker reopen
// right where the pick lives. Not sent to the backend.
export interface BoxSelection {
  type: 'none' | 'all' | 'folder' | 'file';
  id: string | null;
  name: string | null;
  path?: BoxPathItem[];
}

// Tableau dashboards as returned by /focus_mode/user_accessible_items —
// normalized to {id, name} the same way Box items are.
export interface TableauDashboard {
  id: string;
  name: string;
}

const normalizeTableauDashboard = (raw: any): TableauDashboard => ({
  id: String(raw?.dashboard_id ?? ''),
  name: String(raw?.dashboard_name ?? 'Unnamed'),
});

// Committed Tableau/Salesforce picks — same 'none'/'all'/specific shape as
// BoxSelection. For Salesforce, id is the raw option key (e.g. "project")
// and name is its capitalized display form. value is the table the backend
// needs (salesforceOptions[id], e.g. "opportunity"), saved with the pick so
// a pick restored for a reopened chat still sends it before the Salesforce
// list has been loaded again; the payload prefers a freshly loaded
// salesforceOptions[id] when there is one.
export interface TableauSelection {
  type: 'none' | 'all' | 'dashboard';
  id: string | null;
  name: string | null;
}

export interface SalesforceSelection {
  type: 'none' | 'all' | 'item';
  id: string | null;
  name: string | null;
  value?: string | null;
}

const EMPTY_BOX: BoxSelection = { type: 'none', id: null, name: null };
const EMPTY_TABLEAU: TableauSelection = { type: 'none', id: null, name: null };
const EMPTY_SALESFORCE: SalesforceSelection = { type: 'none', id: null, name: null };

export const useFocusMode = (getFocusUserId: () => string) => {
  // Which source's picker modal is currently visible — null means closed.
  // Box/Tableau/Salesforce can each hold their own selection independently
  // (see boxSelection/tableauSelection/salesforceSelection below); this only
  // tracks which ONE of them is currently being shown in the modal.
  const [openFocusSource, setOpenFocusSource] = useState<FocusSubType | null>(null);
  const [focusLoading, setFocusLoading] = useState(false);
  const [focusError, setFocusError] = useState('');

  // ── Box browsing state ────────────────────────────────────────────────────
  // Transient — reset every time Box's modal opens (no caching, matches the
  // rest of focus mode's "always fetch fresh" behavior).
  const [focusInput, setFocusInput]     = useState('');
  const [focusItems, setFocusItems]     = useState<BoxItem[]>([]);
  const [boxRootItems, setBoxRootItems] = useState<BoxItem[]>([]);
  const [focusPath, setFocusPath]       = useState<BoxPathItem[]>([]);

  // ── Tableau / Salesforce lists ────────────────────────────────────────────
  const [tableauItems, setTableauItems]         = useState<TableauDashboard[]>([]);
  const [salesforceOptions, setSalesforceOptions] = useState<Record<string, string>>({});

  // ── Per-source final selections ───────────────────────────────────────────
  // Independent so multiple sources can each hold their own pick at the same
  // time (e.g. a Box file AND a Tableau dashboard both selected).
  const [boxSelection, setBoxSelection]             = useState<BoxSelection>(EMPTY_BOX);
  const [tableauSelection, setTableauSelection]     = useState<TableauSelection>(EMPTY_TABLEAU);
  const [salesforceSelection, setSalesforceSelection] = useState<SalesforceSelection>(EMPTY_SALESFORCE);

  // ── The list on screen has one owner: the request loading it ─────────────
  // Every navigation (opening a picker, opening a folder, any breadcrumb,
  // closing, clearing) cancels the previous request first, and a cancelled
  // request changes nothing: a slow folder reply can't replace the list the
  // user has since moved to, or switch off a newer request's spinner.
  const listingRequestRef = useRef<AbortController | null>(null);

  const startListing = (): AbortSignal => {
    listingRequestRef.current?.abort();
    const request = new AbortController();
    listingRequestRef.current = request;
    return request.signal;
  };

  const cancelListing = () => {
    if (!listingRequestRef.current) return;
    listingRequestRef.current.abort();
    listingRequestRef.current = null;
    setFocusLoading(false);
  };

  useEffect(() => () => listingRequestRef.current?.abort(), []);

  // ── API: fetch ALL source items (box + tableau + salesforce) ──────────────
  // When the Box picker is the one opening, its list shows the top-level Box
  // items (or nothing, if loading fails). reopenAt is the folder trail of a
  // committed Box pick inside a folder: that folder is requested alongside
  // the lists, by the same owner (so any navigation meanwhile cancels it
  // too), and once the lists have loaded the picker moves into it.
  const fetchAllFocusItems = async (forBoxPicker: boolean, reopenAt: BoxPathItem[] = []) => {
    const showBoxItems = (items: BoxItem[]) => {
      if (forBoxPicker) setFocusItems(items);
    };
    if (!FOCUS_BASE_URL) {
      cancelListing();
      setFocusError('Missing REACT_APP_FOCUS_MODE_URL.');
      showBoxItems([]);
      return;
    }
    const userId = getFocusUserId();
    if (!userId) {
      cancelListing();
      setFocusError('Missing user id for focus mode.');
      showBoxItems([]);
      return;
    }

    const signal = startListing();
    setFocusLoading(true);
    setFocusError('');
    const savedFolderRequest = forBoxPicker && reopenAt.length > 0
      ? loadBoxFolderQuietly(reopenAt[reopenAt.length - 1].id, signal)
      : null;
    try {
      const data = await focusModeApi.getAllItems(signal);
      if (signal.aborted) return;

      const box        = Array.isArray(data.box)     ? data.box.map(normalizeBoxItem) : [];
      const tableau    = Array.isArray(data.tableau)  ? data.tableau.map(normalizeTableauDashboard) : [];
      const salesforce = (data.salesforce && typeof data.salesforce === 'object')
        ? (data.salesforce as Record<string, string>)
        : {};

      setBoxRootItems(box);
      setTableauItems(tableau);
      setSalesforceOptions(salesforce);
      showBoxItems(box);
    } catch (e) {
      if (signal.aborted) return;
      console.error('[focus-mode] fetchAllFocusItems error:', e);
      setFocusError('Failed to load focus items.');
      showBoxItems([]);
      return;
    } finally {
      if (!signal.aborted) setFocusLoading(false);
    }

    if (!savedFolderRequest) return;
    const folderItems = await savedFolderRequest;
    if (signal.aborted) return;
    // Without the folder (the failure is reported in loadBoxFolderQuietly),
    // the picker stays at the top level.
    if (folderItems) {
      setFocusPath(reopenAt);
      setFocusItems(folderItems);
    }
  };

  // ── API: fetch Box items for sub-folder navigation ─────────────────────────
  const fetchBoxItems = async (folderId: string) => {
    if (!FOCUS_BASE_URL) { cancelListing(); setFocusError('Missing REACT_APP_FOCUS_MODE_URL.'); return; }
    const userId = getFocusUserId();
    if (!userId) { cancelListing(); setFocusError('Missing user id for focus mode.'); return; }

    const signal = startListing();
    setFocusLoading(true);
    setFocusError('');
    try {
      const data = await focusModeApi.getBoxFolderItems(folderId, signal);
      if (signal.aborted) return;
      const items = Array.isArray(data) ? data : Array.isArray(data?.box) ? data.box : [];
      setFocusItems(items.map(normalizeBoxItem));
    } catch (e) {
      if (signal.aborted) return;
      console.error('[focus-mode] fetchBoxItems error:', e);
      setFocusError('Failed to load focus items.');
    } finally {
      if (!signal.aborted) setFocusLoading(false);
    }
  };

  // ── API: one Box folder's items, for reopening the picker where the
  // committed pick lives. Quiet on purpose — never throws and never touches
  // focusLoading/focusError: if it fails, the picker just stays at the top
  // level of Box, exactly as before this existed. A cancelled request (the
  // user moved on) is not a failure, so it isn't reported. ─────────────────
  const loadBoxFolderQuietly = async (folderId: string, signal: AbortSignal): Promise<BoxItem[] | null> => {
    try {
      const data = await focusModeApi.getBoxFolderItems(folderId, signal);
      const items = Array.isArray(data) ? data : Array.isArray(data?.box) ? data.box : null;
      return items ? items.map(normalizeBoxItem) : null;
    } catch (e) {
      if (!signal.aborted) console.warn('[chat-selections] Could not load the saved Box folder:', e);
      return null;
    }
  };

  // ── Open (or reopen) a source's modal — always fetches fresh (no caching) ─
  // Box with a specific file/folder picked inside a folder reopens in that
  // folder (breadcrumb and all) instead of the top level. Its folder is
  // requested alongside the usual lists, not after them, so it's normally
  // ready the moment they are (see fetchAllFocusItems).
  const openFocusSourceModal = async (source: FocusSubType) => {
    setOpenFocusSource(source);
    setFocusPath([]);
    setFocusInput('');
    const reopenAt =
      source === 'box' && (boxSelection.type === 'file' || boxSelection.type === 'folder')
        ? boxSelection.path ?? []
        : [];
    await fetchAllFocusItems(source === 'box', reopenAt);
  };

  // Closing a picker (×, Cancel, or Submit) stops whatever it was loading.
  const closeFocusModal = () => {
    cancelListing();
    setOpenFocusSource(null);
  };

  // ── Box: pure navigation into a folder — no longer touches boxSelection.
  // Picking a folder/file as the chat target is now a separate, staged
  // choice made inside BoxPanel and only committed via submitBoxSelection,
  // so browsing around never changes what's actually selected until Submit. ─
  const navigateIntoBoxFolder = async (item: BoxItem) => {
    if (!item.id) return;
    setFocusPath((prev) => [...prev, { id: item.id, name: item.name }]);
    await fetchBoxItems(item.id);
  };

  // ── Box: jump to a breadcrumb segment (index -1 = "Box" root) ─────────────
  const navigateToBoxBreadcrumb = async (index: number) => {
    if (index < 0) {
      cancelListing();
      setFocusPath([]);
      setFocusItems(boxRootItems);
      return;
    }
    const target = focusPath[index];
    if (!target) return;
    setFocusPath((prev) => prev.slice(0, index + 1));
    await fetchBoxItems(target.id);
  };

  // ── Box: commit BoxPanel's staged pick (its Submit button) and close ──────
  const submitBoxSelection = (selection: BoxSelection) => {
    setBoxSelection(selection);
    closeFocusModal();
  };

  // ── Tableau: commit TableauPanel's staged pick (its Submit button) ────────
  const submitTableauSelection = (selection: TableauSelection) => {
    setTableauSelection(selection);
    closeFocusModal();
  };

  // ── Salesforce: commit SalesforcePanel's staged pick (its Submit button) ──
  const submitSalesforceSelection = (selection: SalesforceSelection) => {
    setSalesforceSelection(selection);
    closeFocusModal();
  };

  // ── Restore the picks saved for a reopened chat (see chatSelectionsStorage)
  // Sets them directly: no picker opens and nothing is fetched. null means
  // that source isn't part of the restored selection. ─────────────────────
  const restoreFocusSelections = (saved: {
    box: BoxSelection | null;
    tableau: TableauSelection | null;
    salesforce: SalesforceSelection | null;
  }) => {
    setBoxSelection(saved.box ?? EMPTY_BOX);
    setTableauSelection(saved.tableau ?? EMPTY_TABLEAU);
    setSalesforceSelection(saved.salesforce ?? EMPTY_SALESFORCE);
  };

  // ── Clear one source's selection (e.g. removed from the multi-select) ────
  // Closes its modal too, if it was the one open.
  const clearFocusSelection = (source: FocusSubType) => {
    if (source === 'box') setBoxSelection(EMPTY_BOX);
    if (source === 'tableau') setTableauSelection(EMPTY_TABLEAU);
    if (source === 'salesforce') setSalesforceSelection(EMPTY_SALESFORCE);
    if (openFocusSource === source) cancelListing();
    setOpenFocusSource((current) => (current === source ? null : current));
  };

  // ── Clear every source's selection (e.g. reset to "All", or switching to
  // an exclusive mode like Compose/Deep Search/Create Image/File Upload) ────
  const clearAllFocusSelections = () => {
    cancelListing();
    setBoxSelection(EMPTY_BOX);
    setTableauSelection(EMPTY_TABLEAU);
    setSalesforceSelection(EMPTY_SALESFORCE);
    setOpenFocusSource(null);
    setFocusItems([]);
    setBoxRootItems([]);
    setTableauItems([]);
    setSalesforceOptions({});
    setFocusPath([]);
    setFocusInput('');
    setFocusError('');
  };

  return {
    openFocusSource,
    focusLoading,
    focusError,
    focusInput,
    focusItems,
    focusPath,
    tableauItems,
    salesforceOptions,
    boxSelection,
    tableauSelection,
    salesforceSelection,
    setFocusInput,
    openFocusSourceModal,
    closeFocusModal,
    navigateIntoBoxFolder,
    navigateToBoxBreadcrumb,
    submitBoxSelection,
    submitTableauSelection,
    submitSalesforceSelection,
    restoreFocusSelections,
    clearFocusSelection,
    clearAllFocusSelections,
  };
};
